import React, { useEffect, useMemo, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload, X, Loader2, Plus, Trash2, Users, Wand2, Printer,
  Sparkles, Download, FileImage, RotateCcw, RotateCw, ScanFace,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────

const SIZE_PRESETS = [
  { key: "IN_PASSPORT", label: "India Passport 35×45 mm", w: 35, h: 45 },
  { key: "AADHAAR",     label: "Aadhaar 35×45 mm",         w: 35, h: 45 },
  { key: "US_VISA",     label: "US Visa 2×2 in (51×51 mm)", w: 51, h: 51 },
  { key: "SCHENGEN",    label: "Schengen 35×45 mm",         w: 35, h: 45 },
  { key: "UK_PASSPORT", label: "UK Passport 35×45 mm",      w: 35, h: 45 },
  { key: "PAN",         label: "PAN Card 25×35 mm",         w: 25, h: 35 },
  { key: "STAMP",       label: "Stamp 20×25 mm",            w: 20, h: 25 },
  { key: "CUSTOM",      label: "Custom size…",              w: 35, h: 45 },
];

const PAPER_PRESETS = [
  { key: "A4",     label: "A4  (210 × 297 mm)", w: 210, h: 297 },
  { key: "A5",     label: "A5  (148 × 210 mm)", w: 148, h: 210 },
  { key: "4R",     label: "4×6 Photo (102 × 152 mm)", w: 102, h: 152 },
  { key: "LETTER", label: "Letter (216 × 279 mm)", w: 216, h: 279 },
  { key: "CUSTOM", label: "Custom size…", w: 210, h: 297 },
];

const uid = () => Math.random().toString(36).slice(2, 10);

// crop utility — returns the cropped area as a JPEG dataURL matching the
// exact passport aspect. We render at high resolution (target height 1200 px
// so 35×45 mm at 300 DPI = 413×531 px fits with headroom).
async function getCroppedDataUrl(imageSrc, croppedAreaPixels, rotationDeg = 0, adjust = {}) {
  const img = await new Promise((res, rej) => {
    const el = new Image();
    el.crossOrigin = "anonymous";
    el.onload = () => res(el);
    el.onerror = rej;
    el.src = imageSrc;
  });
  const { width: cw, height: ch, x, y } = croppedAreaPixels;
  const canvas = document.createElement("canvas");
  const target = Math.min(2000, Math.max(cw, ch));
  const scale = target / Math.max(cw, ch);
  canvas.width = Math.round(cw * scale);
  canvas.height = Math.round(ch * scale);
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // apply rotation
  if (rotationDeg) {
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotationDeg * Math.PI) / 180);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);
  }
  // brightness/contrast/saturation via CSS filter API
  const b = (adjust.brightness ?? 100) / 100;
  const c = (adjust.contrast ?? 100) / 100;
  const s = (adjust.saturation ?? 100) / 100;
  ctx.filter = `brightness(${b}) contrast(${c}) saturate(${s})`;
  ctx.drawImage(img, x, y, cw, ch, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.92);
}

// ─────────────────────────────────────────────────────────────
//  Layout preview — pure-SVG at fixed pixels-per-mm scale
// ─────────────────────────────────────────────────────────────

function LayoutPreview({ sheet, customers, entriesBySizeKey }) {
  const groups = useMemo(() => {
    // group all copies by physical size + border to mirror backend engine
    const buckets = new Map();
    customers.forEach((c) => {
      if (!c.cropDataUrl) return;
      const key = `${c.w_mm}x${c.h_mm}x${c.hasBorder ? c.borderMm : 0}`;
      const cell_w = c.w_mm + 2 * (c.hasBorder ? c.borderMm : 0);
      const cell_h = c.h_mm + 2 * (c.hasBorder ? c.borderMm : 0);
      if (!buckets.has(key)) buckets.set(key, { cell_w, cell_h, items: [], w_mm: c.w_mm, h_mm: c.h_mm, hasBorder: c.hasBorder, borderMm: c.borderMm });
      for (let i = 0; i < c.copies; i++) buckets.get(key).items.push(c);
    });
    return Array.from(buckets.values());
  }, [customers]);

  const pages = useMemo(() => {
    const result = [];
    for (const g of groups) {
      // Try 4 configurations (photo × sheet rotation) and pick max-fit.
      const evalFit = (sw, sh, cellW, cellH) => {
        const uw = sw - sheet.mLeft - sheet.mRight;
        const uh = sh - sheet.mTop - sheet.mBottom;
        const cols = Math.max(0, Math.floor((uw + sheet.gapH) / (cellW + sheet.gapH)));
        const rows = Math.max(0, Math.floor((uh + sheet.gapV) / (cellH + sheet.gapV)));
        return { cols, rows, sw, sh, cellW, cellH, per: cols * rows };
      };
      const cw = g.cell_w, ch = g.cell_h;
      const candidates = [];
      for (const photoRot of [false, true]) {
        const cellW = photoRot ? ch : cw;
        const cellH = photoRot ? cw : ch;
        for (const sheetRot of [false, true]) {
          const sw = sheetRot ? sheet.paperH : sheet.paperW;
          const sh = sheetRot ? sheet.paperW : sheet.paperH;
          const fit = evalFit(sw, sh, cellW, cellH);
          candidates.push({ ...fit, photoRotated: photoRot });
        }
      }
      const best = candidates.reduce((a, b) => (b.per > a.per ? b : a), candidates[0]);
      if (best.per === 0) continue;
      const perPage = best.per;
      let cursor = 0;
      while (cursor < g.items.length) {
        const page = { sw: best.sw, sh: best.sh, cols: best.cols, rows: best.rows, cells: [], photoRotated: best.photoRotated };
        for (let s = 0; s < perPage && cursor < g.items.length; s++) {
          const col = s % best.cols;
          const row = Math.floor(s / best.cols);
          const x = sheet.mLeft + col * (best.cellW + sheet.gapH);
          const y = sheet.mTop + row * (best.cellH + sheet.gapV);
          page.cells.push({ x, y, cell_w: best.cellW, cell_h: best.cellH, item: g.items[cursor], borderMm: g.hasBorder ? g.borderMm : 0, photoRotated: best.photoRotated });
          cursor++;
        }
        result.push(page);
      }
    }
    return result;
  }, [groups, sheet]);

  const totalCopies = customers.reduce((s, c) => s + (c.cropDataUrl ? c.copies : 0), 0);
  const totalFit = pages.reduce((s, p) => s + p.cells.length, 0);

  return (
    <div className="space-y-4" data-testid="layout-preview">
      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          <span className="font-semibold text-foreground">{totalFit}</span> of{" "}
          <span className="font-semibold text-foreground">{totalCopies}</span> copies · {pages.length} sheet{pages.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="flex flex-wrap gap-6">
        {pages.length === 0 && (
          <div className="w-full py-16 text-center text-sm text-muted-foreground border-2 border-dashed rounded-lg" data-testid="layout-empty">
            Add and crop customer photos to see the sheet preview.
          </div>
        )}
        {pages.map((p, i) => {
          const scale = 480 / Math.max(p.sw, p.sh); // fit within ~480 px longest edge
          const w = p.sw * scale;
          const h = p.sh * scale;
          return (
            <div key={i} className="flex flex-col items-center gap-2" data-testid={`sheet-page-${i}`}>
              <div className="text-xs text-muted-foreground">Sheet {i + 1} · {Math.round(p.sw)}×{Math.round(p.sh)} mm · {p.cols}×{p.rows}{p.photoRotated ? " · photos rotated" : ""}</div>
              <svg width={w} height={h} className="bg-white shadow-md border border-slate-200" style={{ borderRadius: 4 }} data-testid={`sheet-svg-${i}`}>
                {p.cells.map((cell, j) => {
                  const cx = cell.x * scale;
                  const cy = cell.y * scale;
                  const cw = cell.cell_w * scale;
                  const ch = cell.cell_h * scale;
                  const bpx = cell.borderMm * scale;
                  return (
                    <g key={j}>
                      {cell.borderMm > 0 && (
                        <rect x={cx} y={cy} width={cw} height={ch} fill="black" />
                      )}
                      {cell.photoRotated ? (
                        // Rotate the image 90° CW around the centre of the
                        // (inner) cell so the printed photo lands upright
                        // when the sheet is turned physically.
                        <g transform={`rotate(90 ${cx + bpx + (cw - 2*bpx)/2} ${cy + bpx + (ch - 2*bpx)/2})`}>
                          <image
                            href={cell.item.cropDataUrl}
                            x={cx + bpx + (cw - 2*bpx)/2 - (ch - 2*bpx)/2}
                            y={cy + bpx + (ch - 2*bpx)/2 - (cw - 2*bpx)/2}
                            width={ch - 2 * bpx}
                            height={cw - 2 * bpx}
                            preserveAspectRatio="xMidYMid slice"
                          />
                        </g>
                      ) : (
                        <image
                          href={cell.item.cropDataUrl}
                          x={cx + bpx}
                          y={cy + bpx}
                          width={cw - 2 * bpx}
                          height={ch - 2 * bpx}
                          preserveAspectRatio="xMidYMid slice"
                        />
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Crop dialog per photo
// ─────────────────────────────────────────────────────────────

function CropDialog({ open, onOpenChange, customer, updateCustomer, autoDetect }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [adjust, setAdjust] = useState({ brightness: 100, contrast: 100, saturation: 100 });
  const [detecting, setDetecting] = useState(false);
  const aspect = customer?.w_mm / customer?.h_mm;

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotationDeg(customer?.rotationDeg || 0);
    setAdjust(customer?.adjust || { brightness: 100, contrast: 100, saturation: 100 });
  }, [open, customer?.id]);

  const runAutoDetect = async () => {
    if (!customer?.file) return;
    setDetecting(true);
    try {
      const result = await autoDetect(customer.file);
      if (!result?.face) {
        toast.info("No face detected — please crop manually");
        return;
      }
      // Convert face bbox to crop rect that has correct aspect and head fills ~70%.
      const [x1, y1, x2, y2] = result.face;
      const [iw, ih] = result.image_size;
      const faceH = (y2 - y1) * ih;
      const cropH = faceH / 0.70;
      const cropW = cropH * aspect;
      const cxN = (x1 + x2) / 2;
      const cyN = y1 + (y2 - y1) * 0.42; // eye line ~42% from top of face box
      // convert to react-easy-crop crop coordinates:
      //   image displayed with `cover`; crop.x/y is offset in pixels of the
      //   displayed image relative to viewport. We approximate by using zoom
      //   and crop offset in image-pixel space, then translate to displayed
      //   percentages. Simpler: set zoom via displayed area ratio.
      const targetZoom = Math.max(1, Math.min(iw / cropW, ih / cropH));
      setZoom(targetZoom);
      // crop centre in image px:
      const centrePxX = cxN * iw;
      const centrePxY = cyN * ih;
      // offset from image centre — needs to translate to react-easy-crop's units.
      // react-easy-crop uses translation in display pixels. We rely on
      // onCropComplete finalising via croppedAreaPixels — nudging crop is
      // approximate; the exact final crop still respects the aspect ratio.
      setCrop({ x: -(centrePxX - iw / 2) * targetZoom * 0.5, y: -(centrePxY - ih / 2) * targetZoom * 0.5 });
      toast.success("Face detected — fine-tune if needed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Auto-detect failed");
    } finally {
      setDetecting(false);
    }
  };

  const onCropComplete = (_, area) => setCroppedAreaPixels(area);

  const confirm = async () => {
    if (!croppedAreaPixels) return;
    const dataUrl = await getCroppedDataUrl(customer.dataUrl, croppedAreaPixels, rotationDeg, adjust);
    updateCustomer({ cropDataUrl: dataUrl, rotationDeg, adjust });
    onOpenChange(false);
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Crop {customer.name || "customer"} · {customer.w_mm}×{customer.h_mm} mm</DialogTitle>
          <DialogDescription>Drag to move · pinch/wheel to zoom · aspect is locked to the passport size.</DialogDescription>
        </DialogHeader>
        <div className="relative bg-slate-900 rounded-md overflow-hidden" style={{ height: 420 }}>
          <Cropper
            image={customer.dataUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotationDeg}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
          />
        </div>
        <div className="grid gap-4 md:grid-cols-2 pt-2">
          <div className="space-y-1">
            <Label className="text-xs">Zoom</Label>
            <Slider min={1} max={5} step={0.01} value={[zoom]} onValueChange={(v) => setZoom(v[0])} data-testid="crop-zoom" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Rotation</Label>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setRotationDeg((r) => (r - 90 + 360) % 360)} data-testid="crop-rotate-ccw"><RotateCcw className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setRotationDeg((r) => (r + 90) % 360)} data-testid="crop-rotate-cw"><RotateCw className="h-4 w-4" /></Button>
              <span className="text-xs text-muted-foreground">{rotationDeg}°</span>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Brightness · {adjust.brightness}%</Label>
            <Slider min={40} max={160} step={1} value={[adjust.brightness]} onValueChange={(v) => setAdjust((a) => ({ ...a, brightness: v[0] }))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Contrast · {adjust.contrast}%</Label>
            <Slider min={40} max={160} step={1} value={[adjust.contrast]} onValueChange={(v) => setAdjust((a) => ({ ...a, contrast: v[0] }))} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs">Saturation · {adjust.saturation}%</Label>
            <Slider min={0} max={200} step={1} value={[adjust.saturation]} onValueChange={(v) => setAdjust((a) => ({ ...a, saturation: v[0] }))} />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={runAutoDetect} disabled={detecting} data-testid="crop-auto-face">
            {detecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanFace className="h-4 w-4 mr-2" />}
            Auto-detect face
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={confirm} data-testid="crop-confirm">Apply crop</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────

export default function PassportPhoto() {
  const [customers, setCustomers] = useState(() => [{
    id: uid(), name: "", file: null, dataUrl: null, cropDataUrl: null,
    size: "IN_PASSPORT", w_mm: 35, h_mm: 45, customW: 35, customH: 45,
    copies: 8, rotationDeg: 0, adjust: { brightness: 100, contrast: 100, saturation: 100 },
    hasBorder: true, borderMm: 0.3,
  }]);

  const [paperKey, setPaperKey] = useState("A4");
  const [paperW, setPaperW] = useState(210);
  const [paperH, setPaperH] = useState(297);
  const [margins, setMargins] = useState({ mTop: 5, mRight: 5, mBottom: 5, mLeft: 5 });
  const [gaps, setGaps] = useState({ gapH: 3, gapV: 3 });
  const [printers, setPrinters] = useState([]);
  const [printerId, setPrinterId] = useState("");
  const [cropIdx, setCropIdx] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [renderMeta, setRenderMeta] = useState(null); // { upload_id, preview_url, pages }
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/printers");
        setPrinters(data);
        const def = data.find((p) => p.name?.toLowerCase()?.includes("photo")) ||
                    data.find((p) => p.is_default) || data[0];
        if (def) setPrinterId(def.id);
      } catch { /* no printers yet */ }
    })();
  }, []);

  const sheet = useMemo(() => ({
    paperW, paperH,
    mTop: margins.mTop, mRight: margins.mRight, mBottom: margins.mBottom, mLeft: margins.mLeft,
    gapH: gaps.gapH, gapV: gaps.gapV,
  }), [paperW, paperH, margins, gaps]);

  const updateCustomer = (id, patch) => {
    setCustomers((cs) => cs.map((c) => c.id === id ? { ...c, ...patch } : c));
    setRenderMeta(null); // invalidate stale preview
  };

  const removeCustomer = (id) => setCustomers((cs) => cs.length > 1 ? cs.filter((c) => c.id !== id) : cs);

  const addCustomer = () => {
    setCustomers((cs) => [...cs, {
      id: uid(), name: "", file: null, dataUrl: null, cropDataUrl: null,
      size: cs[cs.length - 1]?.size || "IN_PASSPORT",
      w_mm: cs[cs.length - 1]?.w_mm || 35,
      h_mm: cs[cs.length - 1]?.h_mm || 45,
      customW: 35, customH: 45,
      copies: 8, rotationDeg: 0,
      adjust: { brightness: 100, contrast: 100, saturation: 100 },
      hasBorder: cs[cs.length - 1]?.hasBorder ?? true,
      borderMm: cs[cs.length - 1]?.borderMm ?? 0.3,
    }]);
  };

  const onPickFile = (id, file) => {
    if (!file) return;
    if (!/(jpe?g|png)$/i.test(file.name)) return toast.error("JPG or PNG only");
    if (file.size > 15 * 1024 * 1024) return toast.error("Max 15 MB per photo");
    const reader = new FileReader();
    reader.onload = (e) => {
      updateCustomer(id, { file, dataUrl: e.target.result, cropDataUrl: null });
      // open crop dialog immediately for a smooth flow
      const idx = customers.findIndex((c) => c.id === id);
      setCropIdx(idx >= 0 ? idx : null);
    };
    reader.readAsDataURL(file);
  };

  const onSizeChange = (id, key) => {
    const preset = SIZE_PRESETS.find((s) => s.key === key);
    if (!preset) return;
    updateCustomer(id, {
      size: key,
      w_mm: preset.w, h_mm: preset.h,
      cropDataUrl: null, // aspect changed → force re-crop
    });
  };

  const onPaperChange = (key) => {
    const preset = PAPER_PRESETS.find((p) => p.key === key);
    setPaperKey(key);
    if (preset && key !== "CUSTOM") {
      setPaperW(preset.w);
      setPaperH(preset.h);
    }
    // 4×6 photo paper is small — use tight defaults so the classic 8-photo
    // arrangement fits on one sheet even with the black cut border ON.
    if (key === "4R") {
      setMargins({ mTop: 2, mRight: 2, mBottom: 2, mLeft: 2 });
      setGaps({ gapH: 1, gapV: 1 });
    }
    setRenderMeta(null);
  };

  const autoDetectFace = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/passport-editor/detect-face", fd);
    return data;
  };

  const generateSheet = async () => {
    const withCrops = customers.filter((c) => c.cropDataUrl);
    if (!withCrops.length) {
      toast.error("Add and crop at least one photo");
      return;
    }
    setRendering(true);
    setRenderMeta(null);
    try {
      const body = {
        spec: {
          paper_w_mm: paperW, paper_h_mm: paperH,
          margin_top_mm: margins.mTop, margin_right_mm: margins.mRight,
          margin_bottom_mm: margins.mBottom, margin_left_mm: margins.mLeft,
          gap_h_mm: gaps.gapH, gap_v_mm: gaps.gapV,
        },
        photos: withCrops.map((c) => ({
          crop_data_url: c.cropDataUrl,
          w_mm: c.w_mm, h_mm: c.h_mm,
          copies: c.copies,
          has_border: c.hasBorder, border_mm: c.borderMm, border_color: [0, 0, 0],
          adjust: {}, rotation_deg: 0, background: "as_is",
          label: c.name || "Customer",
        })),
        label: withCrops.map((c) => c.name).filter(Boolean).join(", ") || null,
      };
      const { data } = await api.post("/passport-editor/render-sheet", body);
      setRenderMeta(data);
      toast.success(`Sheet ready · ${data.pages} page${data.pages === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Render failed");
    } finally {
      setRendering(false);
    }
  };

  const downloadPdf = async () => {
    if (!renderMeta) return;
    try {
      const resp = await api.get(`/passport-editor/preview/${renderMeta.upload_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `passport_sheet_${renderMeta.upload_id.slice(0, 6)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Download failed");
    }
  };

  const printNow = async () => {
    if (!renderMeta) return;
    setPrinting(true);
    try {
      const body = {
        upload_id: renderMeta.upload_id,
        printer_id: printerId || null,
        paper_size: paperKey.toLowerCase() === "a4" ? "a4"
                  : paperKey.toLowerCase() === "a5" ? "a5"
                  : paperKey.toLowerCase() === "letter" ? "letter"
                  : paperKey.toLowerCase() === "4r" ? "4x6" : "custom",
        label: customers.map((c) => c.name).filter(Boolean).join(", ") || null,
      };
      const { data } = await api.post("/passport-editor/print-now", body);
      toast.success(`Sent to ${data.printer || "printer"} — status: ${data.status}`);
      setRenderMeta(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Print failed");
    } finally {
      setPrinting(false);
    }
  };

  const activeCustomer = cropIdx != null ? customers[cropIdx] : null;

  const totalCopies = customers.reduce((s, c) => s + (c.cropDataUrl ? c.copies : 0), 0);

  return (
    <div className="space-y-6 pb-16" data-testid="passport-editor-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Users className="h-7 w-7 text-blue-600" /> Passport Photo Studio
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Multi-customer sheet builder. Configure exact millimetre sizes, margins and gaps —
            the layout engine packs the maximum photos per sheet automatically. Send straight
            to your printer via the Windows connector.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={addCustomer} variant="outline" data-testid="passport-add-customer">
            <Plus className="h-4 w-4 mr-1" /> Add customer
          </Button>
        </div>
      </div>

      {/* Sheet + margins config */}
      <Card className="p-5 space-y-4" data-testid="sheet-config">
        <div className="flex items-center gap-2 text-sm font-medium">
          <FileImage className="h-4 w-4 text-slate-500" /> Paper &amp; layout
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-xs">Paper size</Label>
            <Select value={paperKey} onValueChange={onPaperChange}>
              <SelectTrigger data-testid="paper-size-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAPER_PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Paper width (mm)</Label>
            <Input type="number" min={50} max={500} value={paperW} onChange={(e) => { setPaperW(Number(e.target.value) || 0); setRenderMeta(null); }} disabled={paperKey !== "CUSTOM"} data-testid="paper-w-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Paper height (mm)</Label>
            <Input type="number" min={50} max={700} value={paperH} onChange={(e) => { setPaperH(Number(e.target.value) || 0); setRenderMeta(null); }} disabled={paperKey !== "CUSTOM"} data-testid="paper-h-input" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[
            ["Top", "mTop"], ["Right", "mRight"], ["Bottom", "mBottom"], ["Left", "mLeft"],
          ].map(([lbl, k]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs">Margin {lbl} (mm)</Label>
              <Input type="number" min={0} max={50} step={0.5}
                value={margins[k]}
                onChange={(e) => { setMargins((m) => ({ ...m, [k]: Number(e.target.value) || 0 })); setRenderMeta(null); }}
                data-testid={`margin-${k}-input`}
              />
            </div>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Gap between photos — horizontal (mm)</Label>
            <Input type="number" min={0} max={20} step={0.5} value={gaps.gapH} onChange={(e) => { setGaps((g) => ({ ...g, gapH: Number(e.target.value) || 0 })); setRenderMeta(null); }} data-testid="gap-h-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Gap between photos — vertical (mm)</Label>
            <Input type="number" min={0} max={20} step={0.5} value={gaps.gapV} onChange={(e) => { setGaps((g) => ({ ...g, gapV: Number(e.target.value) || 0 })); setRenderMeta(null); }} data-testid="gap-v-input" />
          </div>
        </div>
      </Card>

      {/* Customer rows */}
      <div className="space-y-3">
        {customers.map((c, i) => (
          <Card key={c.id} className="p-4" data-testid={`customer-card-${i}`}>
            <div className="grid gap-4 md:grid-cols-[130px_1fr_1fr_auto] items-start">
              {/* thumb */}
              <div>
                {c.cropDataUrl ? (
                  <img src={c.cropDataUrl} alt="" className="w-[120px] h-[150px] object-cover rounded border" data-testid={`customer-thumb-${i}`} />
                ) : c.dataUrl ? (
                  <img src={c.dataUrl} alt="" className="w-[120px] h-[150px] object-cover rounded border opacity-70" />
                ) : (
                  <label className="flex flex-col items-center justify-center w-[120px] h-[150px] border-2 border-dashed rounded-md cursor-pointer text-xs text-muted-foreground hover:bg-slate-50" data-testid={`customer-upload-${i}`}>
                    <Upload className="h-5 w-5 mb-1" />
                    Upload photo
                    <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => onPickFile(c.id, e.target.files?.[0])} />
                  </label>
                )}
              </div>
              {/* fields left */}
              <div className="space-y-2">
                <div className="space-y-1">
                  <Label className="text-xs">Customer name (optional)</Label>
                  <Input value={c.name} onChange={(e) => updateCustomer(c.id, { name: e.target.value })} placeholder="e.g., Anitha K" data-testid={`customer-name-${i}`} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Photo size</Label>
                    <Select value={c.size} onValueChange={(v) => onSizeChange(c.id, v)}>
                      <SelectTrigger data-testid={`customer-size-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SIZE_PRESETS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Copies</Label>
                    <Input type="number" min={1} max={200} value={c.copies} onChange={(e) => updateCustomer(c.id, { copies: Math.max(1, Math.min(200, Number(e.target.value) || 1)) })} data-testid={`customer-copies-${i}`} />
                  </div>
                </div>
                {c.size === "CUSTOM" && (
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" min={10} max={200} value={c.w_mm} onChange={(e) => updateCustomer(c.id, { w_mm: Number(e.target.value) || 35, cropDataUrl: null })} placeholder="Width mm" data-testid={`customer-w-${i}`} />
                    <Input type="number" min={10} max={200} value={c.h_mm} onChange={(e) => updateCustomer(c.id, { h_mm: Number(e.target.value) || 45, cropDataUrl: null })} placeholder="Height mm" data-testid={`customer-h-${i}`} />
                  </div>
                )}
              </div>
              {/* fields right */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <Label className="text-xs">Black cut border</Label>
                  <Switch checked={c.hasBorder} onCheckedChange={(v) => updateCustomer(c.id, { hasBorder: v })} data-testid={`customer-border-${i}`} />
                </div>
                {c.hasBorder && (
                  <div className="space-y-1">
                    <Label className="text-xs">Border thickness · {c.borderMm} mm</Label>
                    <Slider min={0.1} max={2} step={0.1} value={[c.borderMm]} onValueChange={(v) => updateCustomer(c.id, { borderMm: v[0] })} />
                  </div>
                )}
                {c.dataUrl && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setCropIdx(i)} data-testid={`customer-crop-${i}`}>
                      <Wand2 className="h-4 w-4 mr-1" /> {c.cropDataUrl ? "Re-crop" : "Crop photo"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => {
                      const next = ((c.rotationDeg || 0) + 90) % 360;
                      // Rotate ONLY the pixel content (upright orientation). Physical mm size stays the same.
                      if (!c.cropDataUrl) { updateCustomer(c.id, { rotationDeg: next }); return; }
                      const im = new window.Image();
                      im.onload = () => {
                        const cv = document.createElement("canvas");
                        cv.width = im.height; cv.height = im.width;
                        const ctx = cv.getContext("2d");
                        ctx.translate(cv.width / 2, cv.height / 2);
                        ctx.rotate(Math.PI / 2);
                        ctx.drawImage(im, -im.width / 2, -im.height / 2);
                        // Fit back to original aspect (passport w_mm × h_mm) by centre-cropping.
                        const targetAspect = c.w_mm / c.h_mm;
                        const cvAspect = cv.width / cv.height;
                        let sx = 0, sy = 0, sw = cv.width, sh = cv.height;
                        if (cvAspect > targetAspect) {
                          sw = cv.height * targetAspect; sx = (cv.width - sw) / 2;
                        } else {
                          sh = cv.width / targetAspect; sy = (cv.height - sh) / 2;
                        }
                        const final = document.createElement("canvas");
                        final.width = Math.round(sw); final.height = Math.round(sh);
                        final.getContext("2d").drawImage(cv, sx, sy, sw, sh, 0, 0, final.width, final.height);
                        updateCustomer(c.id, {
                          cropDataUrl: final.toDataURL("image/jpeg", 0.92),
                          rotationDeg: next,
                        });
                      };
                      im.src = c.cropDataUrl;
                    }} data-testid={`customer-rotate-${i}`}>
                      <RotateCw className="h-4 w-4 mr-1" /> Rotate 90°
                    </Button>
                  </div>
                )}
              </div>
              {/* remove */}
              <div className="flex md:justify-end">
                <Button variant="ghost" size="icon" onClick={() => removeCustomer(c.id)} data-testid={`customer-remove-${i}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Layout preview */}
      <Card className="p-5">
        <div className="flex items-center gap-2 text-sm font-medium mb-4">
          <Sparkles className="h-4 w-4 text-blue-600" /> Live sheet preview
        </div>
        <LayoutPreview sheet={sheet} customers={customers} />
      </Card>

      {/* Action bar */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur border rounded-xl shadow-lg p-4 flex flex-wrap items-center gap-3" data-testid="passport-action-bar">
        <div className="text-sm">
          <div className="font-semibold">{totalCopies} photo{totalCopies === 1 ? "" : "s"} queued</div>
          <div className="text-xs text-muted-foreground">Paper: {Math.round(paperW)}×{Math.round(paperH)} mm</div>
        </div>
        <div className="flex-1 min-w-[160px]">
          <Select value={printerId} onValueChange={setPrinterId}>
            <SelectTrigger data-testid="printer-select">
              <SelectValue placeholder={printers.length ? "Select printer" : "No printer connected"} />
            </SelectTrigger>
            <SelectContent>
              {printers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={generateSheet} disabled={rendering} data-testid="passport-generate-btn">
          {rendering ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
          Generate PDF
        </Button>
        <Button variant="secondary" onClick={downloadPdf} disabled={!renderMeta} data-testid="passport-download-btn">
          <Download className="h-4 w-4 mr-1" /> Download
        </Button>
        <Button onClick={printNow} disabled={!renderMeta || printing} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="passport-print-btn">
          {printing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />}
          Print now
        </Button>
      </div>

      <CropDialog
        open={cropIdx != null}
        onOpenChange={(v) => setCropIdx(v ? cropIdx : null)}
        customer={activeCustomer}
        updateCustomer={(patch) => activeCustomer && updateCustomer(activeCustomer.id, patch)}
        autoDetect={autoDetectFace}
      />
    </div>
  );
}
